import classNames from "classnames";
import EmptyState from "@/components/EmptyState";
import { RowHeader, RowLink, TableCardRow } from "@/components/Table";
import { ChangeRequestStatus, type Node } from "@/pages/sections/[sectionId]/change-requests/queries";
import { buildSectionChangeRequestListPath } from "@/pages/sections/[sectionId]/change-requests/_utils";
import { type Path, useNavigate } from "@/router";
import {
  formatChangeRequestRecencyLabel,
  formatChangeRequestSummary,
  getChangeRequestInitials,
  getChangeRequestPersonName
} from "../_utils/changeRequestDisplay";
import DashboardTableSkeleton from "./DashboardTableSkeleton";
import { useIsDashboardStackedTableRows } from "../_hooks/useIsDashboardSingleColumn";
import { DASHBOARD_TABLE_CONTENT_CELL, DASHBOARD_TABLE_META_CELL } from "../_utils/dashboardPanelStyles";
import { getDashboardPreviewTableClassName } from "../_utils/dashboardPreviewTable";

const COLUMN_COUNT = 4;
const CHANGE_REQUEST_ICON = "pending_actions" as const;
const AVATAR_CELL = "w-14 px-4 py-3 align-middle";

function InitialsAvatar({ initials }: { initials: string }) {
  return (
    <div
      aria-hidden="true"
      className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full bg-sys-brand-secondary-container"
      data-testid="pending-change-request-initials-avatar"
    >
      <span className="text-label-large text-sys-brand-on-surface-variant">{initials}</span>
    </div>
  );
}

function ChangeRequestRecencyLabel({ submittedAt }: { submittedAt: string }) {
  const label = formatChangeRequestRecencyLabel(submittedAt);

  return <span className="text-body-small whitespace-nowrap text-sys-brand-on-surface-variant">{label ?? ""}</span>;
}

function ChangeRequestRow({ request, sectionId }: { request: Node; sectionId: string }) {
  const navigate = useNavigate();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const personName = getChangeRequestPersonName(request.person);
  const summary = formatChangeRequestSummary(request.change, request.status);

  const openChangeRequest = () => {
    navigate(
      buildSectionChangeRequestListPath(sectionId, {
        stage: ChangeRequestStatus.PENDING,
        expandChanges: request.id
      }) as Path
    );
  };

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`pending-change-request-row-${request.id}`}
      onClick={openChangeRequest}
      ariaLabel={`Open change request for ${personName}`}
      stackedLayout={isStackedTableRows}
      mobile={
        <div className="flex min-w-0 gap-3">
          <InitialsAvatar initials={getChangeRequestInitials(request.person)} />
          <div className="min-w-0 flex-1">
            <div className="text-body-medium text-sys-brand-on-surface">{personName}</div>
            <div className="text-body-small text-sys-brand-on-surface-variant">{summary}</div>
            <div className="pt-1">
              <ChangeRequestRecencyLabel submittedAt={request.submittedAt} />
            </div>
          </div>
        </div>
      }
    >
      <td className={AVATAR_CELL}>
        <InitialsAvatar initials={getChangeRequestInitials(request.person)} />
      </td>
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "w-[28%] max-w-[10rem] truncate")}>{personName}</td>
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "text-sys-brand-on-surface-variant")}>{summary}</td>
      <td className={DASHBOARD_TABLE_META_CELL}>
        <ChangeRequestRecencyLabel submittedAt={request.submittedAt} />
      </td>
    </TableCardRow>
  );
}

type PendingChangeRequestsTableProps = {
  changeRequests: Node[];
  loading?: boolean;
  totalPendingCount?: number;
  sectionId: string;
};

export default function PendingChangeRequestsTable({
  changeRequests,
  loading = false,
  totalPendingCount = 0,
  sectionId
}: PendingChangeRequestsTableProps) {
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const isEmpty = !loading && changeRequests.length === 0;
  const changeRequestsPath = buildSectionChangeRequestListPath(sectionId, {
    stage: ChangeRequestStatus.PENDING
  }) as Path;

  return (
    <table
      className={getDashboardPreviewTableClassName(isStackedTableRows)}
      data-testid="home-pending-change-requests-table"
      aria-label="Pending change requests"
    >
      {isStackedTableRows ? null : (
        <colgroup>
          <col className="w-14" />
          <col className="w-[28%]" />
          <col />
          <col className="w-28" />
        </colgroup>
      )}
      <tbody>
        <RowHeader
          label="Change Requests"
          count={totalPendingCount}
          columnCount={COLUMN_COUNT}
          testId="home-change-requests-count"
          rowTestId="home-change-requests-header"
        />
        {loading ? (
          <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
        ) : isEmpty ? (
          <tr>
            <td colSpan={COLUMN_COUNT}>
              <EmptyState
                iconName={CHANGE_REQUEST_ICON}
                title="No Pending Change Requests"
                caption="Member and associate updates that need approval will appear here."
                action={{
                  children: "View All",
                  to: "/sections/:sectionId/change-requests",
                  params: { sectionId }
                }}
              />
            </td>
          </tr>
        ) : (
          <>
            {changeRequests.map((request) => (
              <ChangeRequestRow key={request.id} request={request} sectionId={sectionId} />
            ))}
            <RowLink columnCount={COLUMN_COUNT} to={changeRequestsPath} testId="pending-change-requests-view-all">
              View All
            </RowLink>
          </>
        )}
      </tbody>
    </table>
  );
}
